import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * Temple Ambience Context.
 * - Persists ON/OFF + volume in localStorage.
 * - Fades volume on toggle for a smooth crossfade.
 * - Independent from the music player (separate <audio> element).
 * - Does NOT autoplay: only starts after an explicit user gesture.
 */
const Ctx = createContext(null);
const KEY = "kx_temple";
const SRC = "/temple-ambience.mp3"; // meditative drone

const readStored = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
};

export const AmbienceProvider = ({ children }) => {
  const stored = useRef(readStored());
  const [enabled, setEnabled] = useState(false); // never autoplays; user must toggle
  const [volume, setVolume] = useState(stored.current.volume ?? 0.28);
  const audioRef = useRef(null);
  const fadeRef = useRef(null);

  // Init audio element once
  useEffect(() => {
    const a = new Audio(SRC);
    a.loop = true;
    a.preload = "none";
    a.volume = 0;
    audioRef.current = a;
    return () => {
      if (fadeRef.current) clearInterval(fadeRef.current);
      a.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify({ volume }));
  }, [volume]);

  const fadeTo = useCallback((target, duration = 700, onDone) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const start = a.volume;
    const startedAt = performance.now();
    fadeRef.current = setInterval(() => {
      const t = Math.min(1, (performance.now() - startedAt) / duration);
      const v = start + (target - start) * t;
      a.volume = Math.max(0, Math.min(1, v));
      if (t === 1) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
        onDone && onDone();
      }
    }, 40);
  }, []);

  const enable = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.volume = 0;
      await a.play();
      setEnabled(true);
      fadeTo(volume, 900);
    } catch {
      setEnabled(false);
    }
  }, [volume, fadeTo]);

  const disable = useCallback(() => {
    fadeTo(0, 700, () => {
      const a = audioRef.current;
      if (a) a.pause();
      setEnabled(false);
    });
  }, [fadeTo]);

  const toggle = useCallback(() => (enabled ? disable() : enable()), [enabled, enable, disable]);

  const setVolumeSafe = useCallback((v) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolume(clamped);
    const a = audioRef.current;
    if (a && enabled) a.volume = clamped;
  }, [enabled]);

  const value = useMemo(() => ({ enabled, volume, toggle, enable, disable, setVolume: setVolumeSafe }),
    [enabled, volume, toggle, enable, disable, setVolumeSafe]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAmbience = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAmbience must be used within AmbienceProvider");
  return v;
};
